import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfUsePage() {
  // Get current date for "Last updated"
  const lastUpdated = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-[#F9F9F6] noise-bg">
      {/* Header */}
      <header className="bg-[#1A2E4A] text-white py-6 px-8">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl">Digital Law Digest</h1>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>
      
      {/* Content */}
      <main className="py-16 px-8 relative z-10" data-testid="terms-of-use-page">
        <article className="prose-editorial">
          <h1 className="text-4xl mb-8 text-[#1A2E4A]">Terms of Use</h1>
          
          <p className="text-sm text-[#525C6A] mb-8">
            Last updated: {lastUpdated}
          </p>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">About this service</h2>
            <p>
              Digital Law Digest is an automated newsletter service that monitors and summarises developments in data protection, AI regulation, cybersecurity and digital law. It is intended to help legal professionals stay informed of relevant legal and regulatory developments in the UK and EU.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Not legal advice</h2>
            <p>
              The content of Digital Law Digest is provided for information purposes only. It does not constitute legal advice and must not be relied upon as such. Nothing in any edition of Digital Law Digest should be treated as a substitute for independent legal advice from a qualified solicitor or other legal professional.
            </p>
            <p className="mt-4">
              Subscribers who require advice on how any legal development applies to their specific circumstances should seek independent legal advice.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">How content is produced</h2>
            <p>
              Each edition of Digital Law Digest is generated automatically. The service monitors authoritative UK and EU legal and regulatory sources, filters content for relevance to the service's scope, and produces summaries using an AI language model. Summaries are not individually reviewed by a human editor before sending.
            </p>
            <p className="mt-4">
              Each edition carries a disclosure that its content has been generated automatically using AI.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Limitations</h2>
            <p>
              Subscribers should be aware of the following limitations:
            </p>
            <ul>
              <li>
                Summaries are AI-generated and may contain errors, omissions or inaccuracies. They should always be verified against the primary source before being relied upon.
              </li>
              <li>
                Coverage is not comprehensive. The service does not capture all legal and regulatory developments within its scope, and the underlying sources are themselves incomplete.
              </li>
            </ul>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Primary sources</h2>
            <p>
              Each item in Digital Law Digest includes a link to the relevant primary source. Subscribers are strongly encouraged to consult the full primary source — including the full text of any judgment or decision — before relying on any summary.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Subscriber obligations</h2>
            <p>
              By subscribing to Digital Law Digest, subscribers agree not to republish, redistribute or forward newsletter content to third parties without permission.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Intellectual property</h2>
            <p>
              Summaries produced by Digital Law Digest are the intellectual property of Digital Law Digest.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Changes to these terms</h2>
            <p>
              These terms may be updated from time to time. Continued subscription to Digital Law Digest following any update constitutes acceptance of the revised terms.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Contact</h2>
            <p>
              For questions about these terms, please contact{' '}
              <a href="mailto:admin@digitallawdigest.com">admin@digitallawdigest.com</a>.
            </p>
          </section>
        </article>
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t border-[#E2E4E0] py-8 px-8">
        <div className="max-w-3xl mx-auto text-center text-sm text-[#525C6A]">
          <p>&copy; {new Date().getFullYear()} Digital Law Digest. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
